import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowDown, ArrowUp, Braces, Columns3Cog, FileSpreadsheet, FileSearch, GripVertical, LayoutTemplate, Save, Settings2, Wand2 } from 'lucide-react';

const NumericField = ({ id, label, value, step = '0.1', onChange, testId }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-medium text-slate-600">{label}</Label>
    <Input
      id={id}
      type="number"
      step={step}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 rounded-xl border-slate-200 bg-white"
      data-testid={testId}
    />
  </div>
);

const ToggleField = ({ id, label, checked, onCheckedChange, testId }) => (
  <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
    <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
    <Switch id={id} checked={Boolean(checked)} onCheckedChange={onCheckedChange} data-testid={testId} />
  </div>
);

const SECTION_HEIGHT_OPTIONS = [
  { value: 'compact', label: 'Compacta' },
  { value: 'normal', label: 'Normal' },
  { value: 'tall', label: 'Alta' },
];

const SECTION_PRESET_OPTIONS = [
  { value: 'compact', label: 'Compacta' },
  { value: 'institutional', label: 'Institucional' },
  { value: 'signature_wide', label: 'Assinatura ampla' },
  { value: 'header_strong', label: 'Cabeçalho forte' },
];

const resolveSectionHeightClass = (value) => {
  if (value === 'compact') return 'h-20';
  if (value === 'tall') return 'h-36';
  return 'h-28';
};

const resolveSectionSpanClass = (value) => {
  if (Number(value) === 4) return 'md:col-span-4';
  if (Number(value) === 3) return 'md:col-span-3';
  if (Number(value) === 2) return 'md:col-span-2';
  return 'md:col-span-1';
};

export const PayrollTemplateConfigPanel = ({
  documents,
  selectedDocument,
  currentSettings,
  loading,
  saving,
  previewing,
  exporting,
  onSelectDocument,
  presetValue,
  onPresetChange,
  onApplyPreset,
  onFieldChange,
  onUpdateSection,
  onMoveSection,
  onReplaceSections,
  onUpdateColumn,
  onMoveColumn,
  onReplaceColumns,
  onApplyJson,
  onSave,
  onPreviewPdf,
  onExportTestXlsx,
}) => {
  const selectedLabel = documents.find((document) => document.key === selectedDocument)?.label || 'Template';
  const [jsonDraft, setJsonDraft] = useState('');
  const [dragState, setDragState] = useState(null);
  const orderedSections = useMemo(() => ([...(currentSettings?.layout_sections || [])]).sort((left, right) => (left.order ?? 0) - (right.order ?? 0)), [currentSettings?.layout_sections]);
  const orderedColumns = useMemo(() => ([...(currentSettings?.column_schema || [])]).sort((left, right) => (left.order ?? 0) - (right.order ?? 0)), [currentSettings?.column_schema]);
  const layoutSections = useMemo(() => orderedSections.map((section) => ({
    ...section,
    canvas_span: Number(section.canvas_span || ((section.key || '').includes('main') || section.key === 'earnings' ? 2 : 1)),
    canvas_height: section.canvas_height || ((section.key || '').includes('signature') ? 'compact' : 'normal'),
    section_preset: section.section_preset || 'institutional',
  })), [orderedSections]);

  useEffect(() => {
    setJsonDraft(JSON.stringify(currentSettings || {}, null, 2));
  }, [currentSettings, selectedDocument]);

  const reorderItems = (items, sourceKey, targetKey, extraUpdater) => {
    const ordered = [...items];
    const sourceIndex = ordered.findIndex((item) => item.key === sourceKey);
    const targetIndex = ordered.findIndex((item) => item.key === targetKey);
    if (sourceIndex === -1 || targetIndex === -1) return items;
    const [moved] = ordered.splice(sourceIndex, 1);
    ordered.splice(targetIndex, 0, extraUpdater ? extraUpdater(moved) : moved);
    return ordered.map((item, index) => ({ ...item, order: index }));
  };

  const handleSectionDrop = (targetKey) => {
    if (!dragState || dragState.type !== 'section' || dragState.key === targetKey) return;
    onReplaceSections(reorderItems(orderedSections, dragState.key, targetKey));
    setDragState(null);
  };

  const handleColumnDrop = (targetColumn) => {
    if (!dragState || dragState.type !== 'column' || dragState.key === targetColumn.key) return;
    onReplaceColumns(reorderItems(orderedColumns, dragState.key, targetColumn.key, (moved) => ({ ...moved, section_key: targetColumn.section_key })));
    setDragState(null);
  };

  const handleColumnDropToSection = (sectionKey) => {
    if (!dragState || dragState.type !== 'column') return;
    const sourceColumn = orderedColumns.find((column) => column.key === dragState.key);
    if (!sourceColumn) return;
    const targetColumns = orderedColumns.filter((column) => column.section_key === sectionKey);
    const targetKey = targetColumns[targetColumns.length - 1]?.key;
    if (!targetKey) {
      const remaining = orderedColumns.filter((column) => column.key !== dragState.key);
      onReplaceColumns([...remaining, { ...sourceColumn, section_key: sectionKey, order: remaining.length }].map((column, index) => ({ ...column, order: index })));
    } else {
      onReplaceColumns(reorderItems(orderedColumns, dragState.key, targetKey, (moved) => ({ ...moved, section_key: sectionKey })));
    }
    setDragState(null);
  };

  return (
    <Card className="w-full max-w-full overflow-hidden rounded-[28px] border-slate-200 bg-[linear-gradient(135deg,#fffdf8,rgba(255,255,255,0.96))] shadow-sm" data-testid="payroll-template-config-panel">
      <CardHeader className="space-y-2 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="rounded-full bg-slate-900 text-white">Preset global (4 documentos)</Badge>
          <Badge variant="outline">Documentos RH</Badge>
          <Badge variant="outline" data-testid="payroll-template-selected-document-badge">{selectedLabel}</Badge>
        </div>
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="h-4 w-4" /> Configuração de Templates
        </CardTitle>
        <CardDescription>
          Organização empresarial do mesmo rascunho global dos 4 documentos RH: preview operacional, editor visual, JSON avançado e preset global alinhados na mesma estrutura.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 overflow-x-hidden">
        <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="min-w-0 space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Documento alvo</Label>
            <Select value={selectedDocument} onValueChange={onSelectDocument}>
              <SelectTrigger className="h-10 rounded-2xl border-slate-200 bg-white" data-testid="payroll-template-document-select">
                <SelectValue placeholder="Seleccionar documento" />
              </SelectTrigger>
              <SelectContent>
                {documents.map((document) => (
                  <SelectItem key={document.key} value={document.key}>{document.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 px-4 py-3 text-xs text-slate-600" data-testid="payroll-template-config-hint">
              A pré-visualização usa sempre o período, escopo, colaborador e folha seleccionados no bloco <span className="font-semibold text-slate-800">Documentos RH</span>. O editor visual e o JSON actuam sobre o <span className="font-semibold text-slate-800">mesmo rascunho</span> antes de guardar.
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3" data-testid="payroll-template-preset-box">
              <Label className="text-xs font-medium text-slate-600">Preset global (4 documentos)</Label>
              <div className="mt-2 flex gap-2">
                <Select value={presetValue} onValueChange={onPresetChange}>
                  <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white" data-testid="payroll-template-preset-select">
                    <SelectValue placeholder="Preset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">A4 compacto</SelectItem>
                    <SelectItem value="institutional">Institucional</SelectItem>
                    <SelectItem value="wide_signatures">Assinaturas amplas</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" className="h-10 rounded-xl px-3 text-xs" onClick={onApplyPreset} data-testid="payroll-template-apply-preset-button">
                  <Wand2 className="mr-1.5 h-4 w-4" /> Aplicar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 xl:grid-cols-3" data-testid="payroll-template-structure-summary">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">1. Contexto da pré-visualização</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">O preview nasce do bloco Documentos RH</p>
            <p className="mt-1 text-xs text-slate-600">Período, escopo, colaborador e folha seleccionados em Documentos RH são a base da pré-visualização PDF/Excel de teste.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">2. Mesmo rascunho</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">Editor visual + JSON avançado</p>
            <p className="mt-1 text-xs text-slate-600">As alterações no editor visual e no JSON actuam sobre a mesma configuração temporária até ao momento do Guardar.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">3. Preset empresarial</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">Preset global (4 documentos)</p>
            <p className="mt-1 text-xs text-slate-600">O preset é global aos 4 documentos RH; o selector acima apenas define qual documento está em foco dentro desse mesmo pacote global.</p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500" data-testid="payroll-template-config-loading-state">
            A carregar configuração global dos templates...
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="min-w-0 space-y-4">
              <div className="grid gap-3 xl:grid-cols-3">
                <div className="rounded-3xl border border-slate-200 bg-white p-3">
                  <h3 className="text-sm font-semibold text-slate-900">Margens e posicionamento</h3>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-2">
                  <NumericField id="template-left-margin" label="Margem esquerda (cm)" value={currentSettings?.left_margin_cm} onChange={(value) => onFieldChange('left_margin_cm', value)} testId="template-setting-left-margin-input" />
                  <NumericField id="template-right-margin" label="Margem direita (cm)" value={currentSettings?.right_margin_cm} onChange={(value) => onFieldChange('right_margin_cm', value)} testId="template-setting-right-margin-input" />
                  <NumericField id="template-top-margin" label="Margem superior (cm)" value={currentSettings?.top_margin_cm} onChange={(value) => onFieldChange('top_margin_cm', value)} testId="template-setting-top-margin-input" />
                  <NumericField id="template-bottom-margin" label="Margem inferior (cm)" value={currentSettings?.bottom_margin_cm} onChange={(value) => onFieldChange('bottom_margin_cm', value)} testId="template-setting-bottom-margin-input" />
                  <NumericField id="template-table-margin" label="Margem lateral da tabela (cm)" value={currentSettings?.table_side_margin_cm} onChange={(value) => onFieldChange('table_side_margin_cm', value)} testId="template-setting-table-margin-input" />
                  <NumericField id="template-header-offset" label="Offset do header (mm)" value={currentSettings?.header_top_offset_mm} onChange={(value) => onFieldChange('header_top_offset_mm', value)} testId="template-setting-header-offset-input" />
                  <NumericField id="template-split-gap" label="Espaço central / corte (cm)" value={currentSettings?.split_gap_cm} onChange={(value) => onFieldChange('split_gap_cm', value)} testId="template-setting-split-gap-input" />
                  <NumericField id="template-signature-space" label="Altura assinatura (mm)" value={currentSettings?.signature_space_mm} onChange={(value) => onFieldChange('signature_space_mm', value)} testId="template-setting-signature-space-input" />
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-3">
                  <h3 className="text-sm font-semibold text-slate-900">Tipografia e estilos</h3>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-2">
                  <NumericField id="template-title-font" label="Fonte do título (pt)" value={currentSettings?.title_font_size_pt} onChange={(value) => onFieldChange('title_font_size_pt', value)} testId="template-setting-title-font-input" />
                  <NumericField id="template-subtitle-font" label="Fonte do subtítulo (pt)" value={currentSettings?.subtitle_font_size_pt} onChange={(value) => onFieldChange('subtitle_font_size_pt', value)} testId="template-setting-subtitle-font-input" />
                  <NumericField id="template-table-font" label="Fonte da tabela (pt)" value={currentSettings?.table_font_size_pt} onChange={(value) => onFieldChange('table_font_size_pt', value)} testId="template-setting-table-font-input" />
                  <div className="space-y-1.5">
                    <Label htmlFor="template-header-bg" className="text-xs font-medium text-slate-600">Cor header tabela</Label>
                    <Input id="template-header-bg" type="color" value={currentSettings?.table_header_bg_color || '#1E293B'} onChange={(event) => onFieldChange('table_header_bg_color', event.target.value)} className="h-9 rounded-xl border-slate-200 bg-white p-1" data-testid="template-setting-header-bg-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="template-header-text" className="text-xs font-medium text-slate-600">Cor texto header</Label>
                    <Input id="template-header-text" type="color" value={currentSettings?.table_header_text_color || '#FFFFFF'} onChange={(event) => onFieldChange('table_header_text_color', event.target.value)} className="h-9 rounded-xl border-slate-200 bg-white p-1" data-testid="template-setting-header-text-input" />
                  </div>
                </div>
              </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-3">
                  <h3 className="text-sm font-semibold text-slate-900">Comportamento do documento</h3>
                  <div className="mt-3 space-y-2">
                    <ToggleField id="template-show-logo" label="Mostrar logo oficial" checked={currentSettings?.show_logo} onCheckedChange={(checked) => onFieldChange('show_logo', checked)} testId="template-setting-show-logo-toggle" />
                    <ToggleField id="template-show-footer" label="Mostrar rodapé" checked={currentSettings?.show_footer} onCheckedChange={(checked) => onFieldChange('show_footer', checked)} testId="template-setting-show-footer-toggle" />
                    <ToggleField id="template-repeat-header" label="Repetir cabeçalho" checked={currentSettings?.repeat_header} onCheckedChange={(checked) => onFieldChange('repeat_header', checked)} testId="template-setting-repeat-header-toggle" />
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="payroll-template-layout-visual-editor">
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-semibold text-slate-900">Editor visual do mesmo rascunho</h3>
                </div>
                <p className="mt-2 text-xs text-slate-600">Arraste os blocos para reorganizar o layout. Tudo o que fizer aqui fica reflectido no mesmo rascunho que também pode ser ajustado no JSON avançado.</p>
                <div className="mt-3 grid gap-3 md:grid-cols-4" data-testid="payroll-template-layout-canvas">
                  {layoutSections.map((section) => (
                    <div
                      key={section.key}
                      className={`rounded-2xl border border-slate-200 bg-slate-50 p-3 ${resolveSectionHeightClass(section.canvas_height)} ${resolveSectionSpanClass(section.canvas_span)} ${dragState?.type === 'section' && dragState?.key === section.key ? 'ring-2 ring-sky-300' : ''}`}
                      data-testid={`template-layout-canvas-section-${section.key}`}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleSectionDrop(section.key)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{section.label}</p>
                          <p className="mt-1 text-xs text-slate-500">Span {section.canvas_span} · {SECTION_HEIGHT_OPTIONS.find((item) => item.value === section.canvas_height)?.label || 'Normal'} · {SECTION_PRESET_OPTIONS.find((item) => item.value === section.section_preset)?.label || 'Institucional'}</p>
                        </div>
                        <button
                          type="button"
                          draggable
                          onDragStart={() => setDragState({ type: 'section', key: section.key })}
                          onDragEnd={() => setDragState(null)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500"
                          data-testid={`template-layout-drag-handle-${section.key}`}
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="payroll-template-sections-editor">
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-semibold text-slate-900">Editor estrutural do layout</h3>
                </div>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70">
                  <div className="grid grid-cols-[44px_minmax(0,1fr)_78px_102px_126px_72px_96px] gap-2 border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <span></span>
                    <span>Bloco</span>
                    <span>Span</span>
                    <span>Altura</span>
                    <span>Preset</span>
                    <span className="text-center">Visível</span>
                    <span className="text-right">Ordem</span>
                  </div>
                  {layoutSections.map((section, index) => (
                    <div
                      key={section.key}
                      className={`grid grid-cols-[44px_minmax(0,1fr)_78px_102px_126px_72px_96px] items-center gap-2 border-b border-slate-200 px-3 py-2 last:border-b-0 ${dragState?.type === 'section' && dragState?.key === section.key ? 'bg-sky-50' : 'bg-white/70'}`}
                      data-testid={`template-section-row-${section.key}`}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleSectionDrop(section.key)}
                    >
                      <button
                        type="button"
                        draggable
                        onDragStart={() => setDragState({ type: 'section', key: section.key })}
                        onDragEnd={() => setDragState(null)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500"
                        data-testid={`template-section-drag-handle-${section.key}`}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <Input value={section.label || ''} onChange={(event) => onUpdateSection(section.key, { label: event.target.value })} className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-section-label-input-${section.key}`} />
                      <Select value={String(section.canvas_span || 1)} onValueChange={(value) => onUpdateSection(section.key, { canvas_span: Number(value) })}>
                        <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-section-span-select-${section.key}`}>
                          <SelectValue placeholder="Span" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 col</SelectItem>
                          <SelectItem value="2">2 col</SelectItem>
                          <SelectItem value="3">3 col</SelectItem>
                          <SelectItem value="4">4 col</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={section.canvas_height || 'normal'} onValueChange={(value) => onUpdateSection(section.key, { canvas_height: value })}>
                        <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-section-height-select-${section.key}`}>
                          <SelectValue placeholder="Altura" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTION_HEIGHT_OPTIONS.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={section.section_preset || 'institutional'} onValueChange={(value) => onUpdateSection(section.key, { section_preset: value })}>
                        <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-section-preset-select-${section.key}`}>
                          <SelectValue placeholder="Preset" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTION_PRESET_OPTIONS.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center justify-center">
                        <Switch checked={Boolean(section.visible)} onCheckedChange={(checked) => onUpdateSection(section.key, { visible: checked })} data-testid={`template-section-visible-toggle-${section.key}`} />
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMoveSection(section.key, 'up')} disabled={index === 0} data-testid={`template-section-move-up-${section.key}`}>
                            <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMoveSection(section.key, 'down')} disabled={index === orderedSections.length - 1} data-testid={`template-section-move-down-${section.key}`}>
                            <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="payroll-template-columns-editor">
                <div className="flex items-center gap-2">
                  <Columns3Cog className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-semibold text-slate-900">Colunas / campos</h3>
                </div>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70">
                  <div className="grid grid-cols-[44px_minmax(0,1.25fr)_108px_170px_72px_112px] gap-2 border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <span></span>
                    <span>Campo</span>
                    <span>Largura</span>
                    <span>Bloco</span>
                    <span className="text-center">Visível</span>
                    <span className="text-right">Ordem</span>
                  </div>
                  {orderedColumns.map((column, index) => (
                    <div
                      key={column.key}
                      className={`grid grid-cols-[44px_minmax(0,1.25fr)_108px_170px_72px_112px] items-center gap-2 border-b border-slate-200 px-3 py-2 last:border-b-0 ${dragState?.type === 'column' && dragState?.key === column.key ? 'bg-sky-50' : 'bg-white/70'}`}
                      data-testid={`template-column-row-${column.key}`}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleColumnDrop(column)}
                    >
                      <button
                        type="button"
                        draggable
                        onDragStart={() => setDragState({ type: 'column', key: column.key })}
                        onDragEnd={() => setDragState(null)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500"
                        data-testid={`template-column-drag-handle-${column.key}`}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <Input value={column.label || ''} onChange={(event) => onUpdateColumn(column.key, { label: event.target.value })} className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-column-label-input-${column.key}`} />
                      <Input type="number" step="0.1" value={column.width_cm ?? ''} onChange={(event) => onUpdateColumn(column.key, { width_cm: event.target.value })} className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-column-width-input-${column.key}`} />
                      <div onDragOver={(event) => event.preventDefault()} onDrop={() => handleColumnDropToSection(column.section_key)}>
                        <Select value={column.section_key} onValueChange={(value) => onUpdateColumn(column.key, { section_key: value })}>
                          <SelectTrigger className="h-8 rounded-lg border-slate-200 bg-white text-sm" data-testid={`template-column-section-select-${column.key}`}>
                            <SelectValue placeholder="Bloco" />
                          </SelectTrigger>
                          <SelectContent>
                            {orderedSections.map((section) => (
                              <SelectItem key={section.key} value={section.key}>{section.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-center">
                        <Switch checked={Boolean(column.visible)} onCheckedChange={(checked) => onUpdateColumn(column.key, { visible: checked })} data-testid={`template-column-visible-toggle-${column.key}`} />
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMoveColumn(column.key, 'up')} disabled={index === 0} data-testid={`template-column-move-up-${column.key}`}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMoveColumn(column.key, 'down')} disabled={index === orderedColumns.length - 1} data-testid={`template-column-move-down-${column.key}`}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="min-w-0 space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="payroll-template-json-editor-card">
                <div className="flex items-center gap-2">
                  <Braces className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-semibold text-slate-900">JSON avançado do mesmo rascunho</h3>
                </div>
                <p className="mt-2 text-xs text-slate-600">
                  Edite a estrutura completa do mesmo rascunho global. Isto controla blocos, colunas, labels, larguras e visibilidade exactamente como o editor visual.
                </p>
                <Textarea
                  value={jsonDraft}
                  onChange={(event) => setJsonDraft(event.target.value)}
                  className="mt-3 min-h-[320px] rounded-2xl border-slate-200 font-mono text-xs"
                  data-testid="payroll-template-json-editor"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" className="h-9 rounded-xl px-3 text-xs" onClick={() => onApplyJson(jsonDraft)} data-testid="payroll-template-json-apply-button">
                    Aplicar JSON ao rascunho
                  </Button>
                  <Button type="button" variant="ghost" className="h-9 rounded-xl px-3 text-xs" onClick={() => setJsonDraft(JSON.stringify(currentSettings || {}, null, 2))} data-testid="payroll-template-json-reset-button">
                    Recarregar JSON actual
                  </Button>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid="payroll-template-actions-card">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-900">Acções rápidas</p>
                  <p className="text-xs text-slate-600">Guardar aplica globalmente ao preset dos 4 documentos. A pré-visualização e o Excel de teste usam o rascunho actual e os filtros escolhidos em Documentos RH.</p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button className="h-9 rounded-xl px-3 text-xs" onClick={onSave} disabled={saving} data-testid="payroll-template-save-button">
                    <Save className="mr-1.5 h-4 w-4" /> {saving ? 'A guardar...' : 'Guardar'}
                  </Button>
                  <Button variant="outline" className="h-9 rounded-xl px-3 text-xs" onClick={onPreviewPdf} disabled={previewing} data-testid="payroll-template-preview-button">
                    <FileSearch className="mr-1.5 h-4 w-4" /> {previewing ? 'A gerar...' : 'Pré-visualizar PDF'}
                  </Button>
                  <Button variant="outline" className="h-9 rounded-xl px-3 text-xs" onClick={onExportTestXlsx} disabled={exporting} data-testid="payroll-template-test-export-button">
                    <FileSpreadsheet className="mr-1.5 h-4 w-4" /> {exporting ? 'A exportar...' : 'Exportar Excel de teste'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};